import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'G1-3'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60 // 25분

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string
  concept: string
  misconceptionCode: string
  learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '다면체 구조',
    difficulty: 'easy',
    question: '사각기둥의 면의 수는 몇 개인가요?',
    choices: ['4개', '5개', '6개', '8개'],
    answer: 2,
    explanation: 'n각기둥의 면의 수 = n+2. 사각기둥(n=4): 4+2=6개',
    concept: '각기둥 면·꼭짓점·모서리', misconceptionCode: 'GP01', learnStepId: 'GC01',
  },
  {
    id: 'Q02', number: 2, category: '다면체 구조',
    difficulty: 'easy',
    question: 'n각뿔의 모서리의 수를 구하는 공식은?',
    choices: ['n개', '2n개', '3n개', 'n+2개'],
    answer: 1,
    explanation: 'n각뿔: 면=n+1, 꼭짓점=n+1, 모서리=2n',
    concept: '각뿔 면·꼭짓점·모서리', misconceptionCode: 'GP01', learnStepId: 'GC01',
  },
  {
    id: 'Q03', number: 3, category: '오일러 공식',
    difficulty: 'easy',
    question: '다면체에서 F+V-E의 값은?',
    choices: ['0', '1', '2', '3'],
    answer: 2,
    explanation: '오일러 공식: F(면)+V(꼭짓점)-E(모서리)=2. 모든 볼록 다면체에서 성립!',
    concept: '오일러 공식', misconceptionCode: 'GP01', learnStepId: 'GC01',
  },
  {
    id: 'Q04', number: 4, category: '다면체 구조',
    difficulty: 'easy',
    question: '오각뿔의 꼭짓점의 수는?',
    choices: ['5개', '6개', '7개', '10개'],
    answer: 1,
    explanation: 'n각뿔의 꼭짓점 = n+1. 오각뿔(n=5): 5+1=6개',
    concept: '각뿔 꼭짓점', misconceptionCode: 'GP01', learnStepId: 'GC01',
  },
  {
    id: 'Q05', number: 5, category: '회전체',
    difficulty: 'easy',
    question: '직사각형을 한 변을 축으로 회전시키면 어떤 입체도형이 만들어지나요?',
    choices: ['원뿔', '원기둥', '구', '각기둥'],
    answer: 1,
    explanation: '직사각형의 한 변을 축으로 회전 → 원기둥. 직각삼각형 → 원뿔, 반원 → 구',
    concept: '회전체의 생성', misconceptionCode: 'GP02', learnStepId: 'GC02',
  },
  {
    id: 'Q06', number: 6, category: '회전체 단면',
    difficulty: 'easy',
    question: '원기둥을 회전축에 수직인 평면으로 자를 때 단면의 모양은?',
    choices: ['직사각형', '삼각형', '원', '타원'],
    answer: 2,
    explanation: '원기둥의 수직 단면=원, 평행 단면=직사각형',
    concept: '회전체 단면', misconceptionCode: 'GP02', learnStepId: 'GC02',
  },
  {
    id: 'Q07', number: 7, category: '회전체 단면',
    difficulty: 'medium',
    question: '원뿔을 회전축을 포함하는 평면으로 자를 때 단면의 모양은?',
    choices: ['원', '직사각형', '이등변삼각형', '부채꼴'],
    answer: 2,
    explanation: '원뿔을 회전축을 포함하도록 자르면 이등변삼각형. (두 모선=같음)',
    concept: '원뿔 단면', misconceptionCode: 'GP02', learnStepId: 'GC02',
  },
  {
    id: 'Q08', number: 8, category: '겉넓이',
    difficulty: 'easy',
    question: '반지름 r인 구의 겉넓이 공식은?',
    choices: ['πr²', '2πr²', '4πr²', '(4/3)πr³'],
    answer: 2,
    explanation: '구의 겉넓이 = 4πr². (마지막 선택지는 구의 부피 공식)',
    concept: '구 겉넓이', misconceptionCode: 'GP03', learnStepId: 'GC03',
  },
  {
    id: 'Q09', number: 9, category: '겉넓이',
    difficulty: 'medium',
    question: '반지름 3cm, 높이 4cm인 원기둥의 겉넓이는? (π 사용)',
    choices: ['24π cm²', '42π cm²', '48π cm²', '54π cm²'],
    answer: 1,
    explanation: '겉넓이=2πr²+2πrh=2π×9+2π×3×4=18π+24π=42π cm²',
    concept: '원기둥 겉넓이', misconceptionCode: 'GP03', learnStepId: 'GC03',
  },
  {
    id: 'Q10', number: 10, category: '겉넓이',
    difficulty: 'medium',
    question: '밑면 반지름 3cm, 모선 5cm인 원뿔의 겉넓이는?',
    choices: ['9π cm²', '15π cm²', '24π cm²', '30π cm²'],
    answer: 2,
    explanation: '원뿔 겉넓이=πr²+πrl=π×9+π×3×5=9π+15π=24π cm²',
    concept: '원뿔 겉넓이', misconceptionCode: 'GP03', learnStepId: 'GC03',
  },
  {
    id: 'Q11', number: 11, category: '부피',
    difficulty: 'easy',
    question: '원뿔의 부피 공식은?',
    choices: ['πr²h', '(1/2)πr²h', '(1/3)πr²h', '(4/3)πr³'],
    answer: 2,
    explanation: '원뿔 부피=(1/3)πr²h. 같은 밑면·높이 원기둥의 1/3!',
    concept: '원뿔 부피', misconceptionCode: 'GP04', learnStepId: 'GC04',
  },
  {
    id: 'Q12', number: 12, category: '부피',
    difficulty: 'medium',
    question: '반지름 3cm, 높이 4cm인 원뿔의 부피는?',
    choices: ['9π cm³', '12π cm³', '36π cm³', '48π cm³'],
    answer: 1,
    explanation: '원뿔 부피=(1/3)×π×3²×4=(1/3)×36π=12π cm³',
    concept: '원뿔 부피', misconceptionCode: 'GP04', learnStepId: 'GC04',
  },
  {
    id: 'Q13', number: 13, category: '부피',
    difficulty: 'medium',
    question: '반지름 3cm인 구의 부피는?',
    choices: ['9π cm³', '27π cm³', '36π cm³', '108π cm³'],
    answer: 2,
    explanation: '구 부피=(4/3)πr³=(4/3)π×27=36π cm³',
    concept: '구 부피', misconceptionCode: 'GP04', learnStepId: 'GC04',
  },
  {
    id: 'Q14', number: 14, category: '부피 비교',
    difficulty: 'hard',
    question: '같은 밑면과 높이를 가진 원기둥과 원뿔의 부피 비는?',
    choices: ['1 : 1', '1 : 2', '1 : 3', '3 : 1'],
    answer: 3,
    explanation: '원기둥:원뿔 = πr²h : (1/3)πr²h = 3 : 1. 원기둥이 원뿔의 3배!',
    concept: '원기둥·원뿔 부피 비', misconceptionCode: 'GP04', learnStepId: 'GC04',
  },
  {
    id: 'Q15', number: 15, category: '종합',
    difficulty: 'hard',
    question: '반지름 6cm인 구의 겉넓이와 부피를 각각 바르게 구한 것은?',
    choices: [
      '겉넓이=24π, 부피=36π',
      '겉넓이=144π, 부피=288π',
      '겉넓이=36π, 부피=144π',
      '겉넓이=144π, 부피=144π',
    ],
    answer: 1,
    explanation: '겉넓이=4π×6²=4π×36=144π cm²\n부피=(4/3)π×6³=(4/3)π×216=288π cm³',
    concept: '구 겉넓이·부피 종합', misconceptionCode: 'GP04', learnStepId: 'GC04',
  },
]

type Phase = 'ready' | 'testing' | 'done'

export default function G1_3TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('ready')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [current, setCurrent] = useState(0)
  const [showExp, setShowExp] = useState<Record<number, boolean>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))

  useEffect(() => {
    if (phase === 'testing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current!); finishTest(); return 0 }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const finishTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const seconds = TIME_LIMIT - timeLeft
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.misconceptionCode)
      .filter((v, i, arr) => arr.indexOf(v) === i)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers.map(a => a ?? -1),
      wrongConceptIds: TEST_QUESTIONS
        .filter((q, i) => answers[i] !== q.answer)
        .map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('done')
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
  const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE

  const diffColor = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' }
  const diffLabel = { easy: '쉬움', medium: '보통', hard: '어려움' }

  // ── 준비 화면 ──
  if (phase === 'ready') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-2xl bg-gradient-to-br from-sky-600 to-blue-700 text-white p-6 mb-6 text-center">
          <p className="text-4xl mb-3">🧊</p>
          <h2 className="text-2xl font-bold mb-2">입체도형 실전 TEST</h2>
          <p className="text-sky-100 text-sm">G1-3 단원 전체 평가</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: '📝', label: '총 문항', value: `${TEST_QUESTIONS.length}문항` },
            { icon: '⏱️', label: '제한 시간', value: '25분' },
            { icon: '🎯', label: '합격 기준', value: `${PASS_SCORE}점 이상` },
            { icon: '📊', label: '난이도', value: '쉬움~어려움' },
          ].map(item => (
            <div key={item.label} className="card border border-slate-200 text-center">
              <p className="text-2xl mb-1">{item.icon}</p>
              <p className="text-xs text-slate-400">{item.label}</p>
              <p className="text-sm font-bold text-slate-800">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="card border border-slate-200 mb-6">
          <h3 className="font-bold text-slate-800 mb-3">📋 출제 범위</h3>
          <div className="space-y-2">
            {[
              { step: 'GC01', title: '다면체의 분류', count: 4, color: 'bg-sky-100 text-sky-700' },
              { step: 'GC02', title: '회전체', count: 3, color: 'bg-violet-100 text-violet-700' },
              { step: 'GC03', title: '겉넓이 계산', count: 3, color: 'bg-orange-100 text-orange-700' },
              { step: 'GC04', title: '부피 계산', count: 5, color: 'bg-teal-100 text-teal-700' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.color}`}>{item.step}</span>
                <span className="text-sm text-slate-700 flex-1">{item.title}</span>
                <span className="text-xs text-slate-400">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => { setPhase('testing'); setTimeLeft(TIME_LIMIT) }}
          className="w-full py-4 bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white rounded-2xl font-bold text-lg transition-all shadow-lg"
        >
          시험 시작하기 🚀
        </button>
      </div>
    )
  }

  // ── 시험 화면 ──
  if (phase === 'testing') {
    const q = TEST_QUESTIONS[current]
    const answered = answers.filter(a => a !== null).length
    return (
      <div className="p-4 max-w-2xl mx-auto">
        {/* 상단 상태바 */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">{current + 1}/{TEST_QUESTIONS.length}</span>
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full transition-all"
                style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${
            timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-sky-100 text-sky-700'
          }`}>
            <Timer size={14} />
            {fmt(timeLeft)}
          </div>
        </div>

        {/* 문제 카드 */}
        <div className="card border-2 border-sky-100 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold bg-sky-600 text-white px-2 py-0.5 rounded-full">Q{q.number}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diffColor[q.difficulty]}`}>
              {diffLabel[q.difficulty]}
            </span>
            <span className="text-xs text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-semibold text-slate-800 leading-relaxed mb-4 whitespace-pre-line">{q.question}</p>
          <div className="space-y-2">
            {q.choices.map((choice, i) => (
              <button
                key={i}
                onClick={() => {
                  const next = [...answers]; next[current] = i; setAnswers(next)
                }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answers[current] === i
                    ? 'border-sky-500 bg-sky-50 text-sky-800'
                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className="font-bold mr-2">{['①', '②', '③', '④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>

        {/* 이동 버튼 */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setCurrent(c => Math.max(0, c - 1))}
            disabled={current === 0}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-sky-600 text-white font-bold text-sm hover:bg-sky-700 flex items-center justify-center gap-1">
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={finishTest}
              disabled={answered < TEST_QUESTIONS.length}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"
            >
              제출하기 ({answered}/{TEST_QUESTIONS.length})
            </button>
          )}
        </div>

        {/* 문항 번호 빠른 이동 */}
        <div className="flex flex-wrap gap-1.5">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-sky-600 text-white' :
                answers[i] !== null ? 'bg-sky-100 text-sky-700' :
                'bg-slate-100 text-slate-400'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── 결과 화면 ──
  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* 결과 헤더 */}
      <div className={`rounded-2xl p-6 mb-5 text-center text-white bg-gradient-to-br ${
        passed ? 'from-emerald-500 to-teal-600' : 'from-slate-600 to-slate-700'
      }`}>
        <p className="text-5xl mb-2">{passed ? '🏆' : '📚'}</p>
        <p className="text-3xl font-black mb-1">{score}점</p>
        <p className="text-lg font-bold opacity-90">{passed ? '합격! 입체도형 마스터!' : '아직 조금 더 연습이 필요해요!'}</p>
        <p className="text-sm opacity-75 mt-1">{correct}/{TEST_QUESTIONS.length} 정답 · {passed ? '✅ 합격' : `❌ 불합격 (기준 ${PASS_SCORE}점)`}</p>
      </div>

      {/* 성취 뱃지 */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: '🏛️', label: '다면체', questions: TEST_QUESTIONS.filter(q => q.learnStepId === 'GC01') },
          { icon: '🔄', label: '회전체', questions: TEST_QUESTIONS.filter(q => q.learnStepId === 'GC02') },
          { icon: '📦', label: '겉넓이', questions: TEST_QUESTIONS.filter(q => q.learnStepId === 'GC03') },
        ].map(group => {
          const groupCorrect = group.questions.filter((q, _) => {
            const idx = TEST_QUESTIONS.findIndex(tq => tq.id === q.id)
            return answers[idx] === q.answer
          }).length
          const pct = Math.round((groupCorrect / group.questions.length) * 100)
          return (
            <div key={group.label} className="card border border-slate-200 text-center">
              <p className="text-xl mb-1">{group.icon}</p>
              <p className="text-xs font-bold text-slate-700">{group.label}</p>
              <p className={`text-sm font-black mt-0.5 ${pct >= 70 ? 'text-emerald-600' : 'text-red-500'}`}>{pct}%</p>
            </div>
          )
        })}
      </div>

      {/* 문제별 결과 */}
      <div className="card border border-slate-200 mb-5">
        <h3 className="font-bold text-slate-800 mb-3">📋 문항별 결과</h3>
        <div className="space-y-2">
          {TEST_QUESTIONS.map((q, i) => {
            const isCorrect = answers[i] === q.answer
            return (
              <div key={q.id}>
                <button
                  onClick={() => setShowExp(s => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  {isCorrect
                    ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                    : <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                  <span className="text-sm font-semibold text-slate-700 flex-1 text-left">Q{q.number}. {q.category}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${diffColor[q.difficulty]}`}>
                    {diffLabel[q.difficulty]}
                  </span>
                  <ChevronDown size={13} className={`text-slate-400 transition-transform ${showExp[i] ? 'rotate-180' : ''}`} />
                </button>
                {showExp[i] && (
                  <div className="mt-1 p-3 bg-white border border-slate-200 rounded-xl text-xs space-y-1.5 ml-2">
                    <p className="text-slate-700 font-medium">{q.question}</p>
                    {!isCorrect && (
                      <p className="text-red-600">
                        내 답: {answers[i] !== null ? q.choices[answers[i]] : '미응답'}
                      </p>
                    )}
                    <p className="text-emerald-700 font-semibold">
                      정답: {q.choices[q.answer]}
                    </p>
                    <p className="text-slate-500 whitespace-pre-line">{q.explanation}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* 오개념 안내 */}
      {!passed && (
        <div className="card border border-amber-200 bg-amber-50 mb-5">
          <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
            <AlertTriangle size={16} /> 취약 개념 안내
          </h3>
          {TEST_QUESTIONS
            .filter((q, i) => answers[i] !== q.answer)
            .map(q => q.misconceptionCode)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map(code => (
              <div key={code} className="flex items-start gap-2 mb-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0 mt-0.5">{code}</span>
                <p className="text-xs text-amber-700">
                  {code === 'GP01' && '각기둥·각뿔 공식 혼동 → GC01 개념 재학습 권장'}
                  {code === 'GP02' && '회전체 단면 혼동 → GC02 개념 재학습 권장'}
                  {code === 'GP03' && '겉넓이 공식 오류 → GC03 개념 재학습 권장'}
                  {code === 'GP04' && '부피 공식 (1/3 누락) → GC04 개념 재학습 권장'}
                </p>
              </div>
            ))}
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <BookOpen size={11} /> 개념 학습 탭에서 해당 단계를 다시 학습해보세요!
          </p>
          {onGoLearn && (
            <div className="mt-2 space-y-1">
              {TEST_QUESTIONS
                .filter((q, i) => answers[i] !== q.answer)
                .map((q, _, arr) => arr.find(x => x.learnStepId === q.learnStepId))
                .filter((q, i, arr) => q && arr.findIndex(x => x?.learnStepId === q.learnStepId) === i)
                .map(q => q && (
                  <button key={q.learnStepId}
                    onClick={() => onGoLearn(q.learnStepId)}
                    className="w-full text-left text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium flex items-center gap-1.5">
                    <BookOpen size={11} /> {q.learnStepId} 개념 다시 학습하기 →
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* 하단 버튼 */}
      <div className="flex gap-3">
        <button
          onClick={() => {
            setPhase('ready')
            setAnswers(Array(TEST_QUESTIONS.length).fill(null))
            setTimeLeft(TIME_LIMIT)
            setCurrent(0)
            setShowExp({})
          }}
          className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2"
        >
          <RotateCcw size={15} /> 다시 풀기
        </button>
        {passed && (
          <div className="flex-1 py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2">
            <Trophy size={15} /> 입체도형 완료! 🎉
          </div>
        )}
      </div>
    </div>
  )
}
